/** The minimized pill (spec section 3). One button; drags from anywhere, restores on a real click. */
import { createPortal } from 'react-dom';
import { Flag } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FEEDBACK_LAYER_CLASS, anchorStyle } from './constants';
import { isolateKeys } from './isolateKeys';
import { restore, setCorner } from './feedbackStore';
import { usePanelDrag } from './usePanelDrag';
import type { Corner } from './constants';

interface Props {
  corner: Corner;
  paused: boolean;
  draft: boolean;
}

export function FeedbackPill({ corner, paused, draft }: Readonly<Props>) {
  const drag = usePanelDrag<HTMLButtonElement>({ onSnap: setCorner, dragFromButtons: true });
  let label = draft ? 'Restore feedback panel (draft in progress)' : 'Restore feedback panel';
  if (paused) label = 'Feedback paused while a dialog is open';

  return createPortal(
    <button
      ref={drag.ref}
      type="button"
      aria-label={label}
      tabIndex={paused ? -1 : undefined}
      data-feedback-pill=""
      data-testid="feedback-pill"
      style={{ ...anchorStyle(corner), ...drag.style }}
      className={cn(
        'fixed inline-flex touch-none items-center gap-1.5 rounded-full bg-foreground px-3 py-1.5 text-xs font-semibold text-background shadow-[var(--shadow-popover)] transition-[translate] duration-150 hover:bg-foreground/90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none',
        drag.dragging ? 'cursor-grabbing' : 'cursor-grab',
        paused && 'pointer-events-none opacity-70',
        FEEDBACK_LAYER_CLASS,
      )}
      {...drag.handlers}
      {...isolateKeys}
      onClick={() => {
        if (drag.consumeDragClick() || paused) return;
        restore();
      }}
    >
      <Flag className="size-3.5" aria-hidden />
      {paused ? 'Feedback paused' : 'Feedback'}
      {draft && <span aria-hidden="true" className="size-2 rounded-full bg-warning ring-2 ring-background" />}
    </button>,
    document.body,
  );
}
